/** Cliente HTTP de la Console superadmin (cross-tenant). */
import { apiRequest } from "../../lib/api";

export interface EmpresaRead {
  id: number;
  nombre: string;
  rut: string;
  email: string;
  direccion: string | null;
  telefono: string | null;
  giro: string | null;
  is_active: boolean;
  /** null = plan completo; lista = módulos contratados. */
  modulos_habilitados: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface UsuarioRead {
  id: number;
  empresa_id: number;
  username: string;
  nombre: string;
  email: string;
  /** Code del rol (canónico o propio del comité). */
  role: string;
  cargo: string | null;
  is_active: boolean;
  is_superadmin: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmpresaConMetricas {
  empresa: EmpresaRead;
  total_usuarios: number;
  total_socios_activos: number;
  total_contratos_vigentes: number;
  lecturas_mes_actual: number;
  cierres_pendientes: number;
  ultima_lectura_at: string | null;
}

export interface EmpresaDetalle {
  empresa: EmpresaRead;
  admins: UsuarioRead[];
  metricas: EmpresaConMetricas;
}

export interface CrearEmpresaPayload {
  nombre: string;
  rut: string;
  email: string;
  direccion?: string | null;
  telefono?: string | null;
  giro?: string | null;
  admin_username: string;
  admin_nombre: string;
  admin_email: string;
  admin_password: string;
}

export interface CrearEmpresaResponse {
  empresa: EmpresaRead;
  admin: UsuarioRead;
}

export interface LoginAsResponse {
  access_token: string;
  empresa_id: number;
  expires_in_seconds: number;
}

export const listarEmpresas = (): Promise<EmpresaConMetricas[]> =>
  apiRequest<EmpresaConMetricas[]>("/api/v1/superadmin/empresas");

export const detalleEmpresa = (empresaId: number): Promise<EmpresaDetalle> =>
  apiRequest<EmpresaDetalle>(`/api/v1/superadmin/empresas/${empresaId}`);

export const crearEmpresa = (payload: CrearEmpresaPayload): Promise<CrearEmpresaResponse> =>
  apiRequest<CrearEmpresaResponse>("/api/v1/superadmin/empresas", {
    method: "POST",
    body: payload,
  });

export const suspenderEmpresa = (empresaId: number): Promise<EmpresaRead> =>
  apiRequest<EmpresaRead>(`/api/v1/superadmin/empresas/${empresaId}/suspender`, {
    method: "POST",
  });

export const reactivarEmpresa = (empresaId: number): Promise<EmpresaRead> =>
  apiRequest<EmpresaRead>(`/api/v1/superadmin/empresas/${empresaId}/reactivar`, {
    method: "POST",
  });

export const loginAsEmpresa = (
  empresaId: number,
  motivo: string,
): Promise<LoginAsResponse> =>
  apiRequest<LoginAsResponse>(`/api/v1/superadmin/empresas/${empresaId}/login-as`, {
    method: "POST",
    body: { motivo },
  });

// ────────────────────────────────────────────────────────────────────────
// Gestión de usuarios por empresa
// ────────────────────────────────────────────────────────────────────────

/**
 * El rol pasa a ser dinámico. Antes era un Literal; ahora es el ``code``
 * de algún rol existente (canónico o custom del tenant). Validar contra
 * la lista que devuelve `listarRolesEmpresa`.
 */
export type RoleUsuario = string;

export type CargoCanonico =
  | "Administrador"
  | "Operador"
  | "Recaudador"
  | "Técnico"
  | "Administrativo"
  | "Tesorero"
  | "Secretario"
  | "Presidente"
  | "Director"
  | "Otro";

export interface CargoSugerido {
  valor: CargoCanonico;
  descripcion: string;
}

export interface CrearUsuarioPayload {
  username: string;
  nombre: string;
  email: string;
  password: string;
  role: RoleUsuario;
  cargo: CargoCanonico;
  cargo_personalizado?: string | null;
}

export interface ActualizarUsuarioPayload {
  nombre?: string;
  email?: string;
  role?: RoleUsuario;
  cargo?: CargoCanonico;
  cargo_personalizado?: string | null;
  is_active?: boolean;
}

export const listarCargosSugeridos = (): Promise<CargoSugerido[]> =>
  apiRequest<CargoSugerido[]>("/api/v1/superadmin/cargos-sugeridos");

export const listarUsuariosEmpresa = (empresaId: number): Promise<UsuarioRead[]> =>
  apiRequest<UsuarioRead[]>(`/api/v1/superadmin/empresas/${empresaId}/usuarios`);

export const crearUsuarioEmpresa = (
  empresaId: number,
  payload: CrearUsuarioPayload,
): Promise<UsuarioRead> =>
  apiRequest<UsuarioRead>(`/api/v1/superadmin/empresas/${empresaId}/usuarios`, {
    method: "POST",
    body: payload,
  });

export const actualizarUsuarioEmpresa = (
  empresaId: number,
  usuarioId: number,
  payload: ActualizarUsuarioPayload,
): Promise<UsuarioRead> =>
  apiRequest<UsuarioRead>(
    `/api/v1/superadmin/empresas/${empresaId}/usuarios/${usuarioId}`,
    { method: "PATCH", body: payload },
  );

export const resetPasswordUsuarioEmpresa = (
  empresaId: number,
  usuarioId: number,
  nuevoPassword: string,
): Promise<UsuarioRead> =>
  apiRequest<UsuarioRead>(
    `/api/v1/superadmin/empresas/${empresaId}/usuarios/${usuarioId}/reset-password`,
    { method: "POST", body: { nuevo_password: nuevoPassword } },
  );

// ────────────────────────────────────────────────────────────────────────
// Roles + permisos
// ────────────────────────────────────────────────────────────────────────

export interface PermisoItem {
  code: string;
  etiqueta: string;
}

export interface PermisoGrupo {
  code: string;
  titulo: string;
  permisos: PermisoItem[];
}

export interface CatalogoPermisos {
  grupos: PermisoGrupo[];
}

export interface RolRead {
  id: number;
  empresa_id: number | null;
  code: string;
  nombre: string;
  descripcion: string | null;
  permisos: string[];
  is_canonico: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CrearRolPayload {
  code: string;
  nombre: string;
  descripcion?: string | null;
  permisos: string[];
}

export interface ActualizarRolPayload {
  nombre?: string;
  descripcion?: string | null;
  permisos?: string[];
  is_active?: boolean;
}

export const obtenerCatalogoPermisos = (): Promise<CatalogoPermisos> =>
  apiRequest<CatalogoPermisos>("/api/v1/superadmin/permisos");

export const listarRolesCanonicos = (): Promise<RolRead[]> =>
  apiRequest<RolRead[]>("/api/v1/superadmin/roles");

export const crearRolCanonico = (payload: CrearRolPayload): Promise<RolRead> =>
  apiRequest<RolRead>("/api/v1/superadmin/roles", {
    method: "POST",
    body: payload,
  });

export const actualizarRol = (
  rolId: number,
  payload: ActualizarRolPayload,
): Promise<RolRead> =>
  apiRequest<RolRead>(`/api/v1/superadmin/roles/${rolId}`, {
    method: "PATCH",
    body: payload,
  });

export const listarRolesEmpresa = (empresaId: number): Promise<RolRead[]> =>
  apiRequest<RolRead[]>(`/api/v1/superadmin/empresas/${empresaId}/roles`);

export const crearRolEmpresa = (
  empresaId: number,
  payload: CrearRolPayload,
): Promise<RolRead> =>
  apiRequest<RolRead>(`/api/v1/superadmin/empresas/${empresaId}/roles`, {
    method: "POST",
    body: payload,
  });

// ────────────────────────────────────────────────────────────────────────
// Módulos contratados por comité
// ────────────────────────────────────────────────────────────────────────

export interface ModuloItem {
  code: string;
  titulo: string;
  descripcion: string;
  requiere: string[];
}

export const listarModulos = (): Promise<ModuloItem[]> =>
  apiRequest<ModuloItem[]>("/api/v1/superadmin/modulos");

export const actualizarModulosEmpresa = (
  empresaId: number,
  modulos: string[] | null,
): Promise<EmpresaRead> =>
  apiRequest<EmpresaRead>(`/api/v1/superadmin/empresas/${empresaId}/modulos`, {
    method: "PUT",
    body: { modulos },
  });

// ────────────────────────────────────────────────────────────────────────
// Accesos de staff a otros comités (personal de oficina)
// ────────────────────────────────────────────────────────────────────────

export interface AccesoComiteRead {
  empresa_id: number;
  empresa_nombre: string;
  role: string;
  created_at: string;
}

const accesosUrl = (empresaId: number, usuarioId: number): string =>
  `/api/v1/superadmin/empresas/${empresaId}/usuarios/${usuarioId}/accesos`;

export const listarAccesosUsuario = (
  empresaId: number,
  usuarioId: number,
): Promise<AccesoComiteRead[]> => apiRequest<AccesoComiteRead[]>(accesosUrl(empresaId, usuarioId));

export const otorgarAccesoUsuario = (
  empresaId: number,
  usuarioId: number,
  payload: { empresa_id: number; role: string },
): Promise<AccesoComiteRead[]> =>
  apiRequest<AccesoComiteRead[]>(accesosUrl(empresaId, usuarioId), {
    method: "POST",
    body: payload,
  });

export const revocarAccesoUsuario = (
  empresaId: number,
  usuarioId: number,
  empresaDestinoId: number,
): Promise<void> =>
  apiRequest<void>(`${accesosUrl(empresaId, usuarioId)}/${empresaDestinoId}`, {
    method: "DELETE",
  });

// ────────────────────────────────────────────────────────────────────────
// Documentos tributarios (DTE): configuración del emisor por comité
// ────────────────────────────────────────────────────────────────────────

export interface ConfiguracionDTE {
  empresa_id: number;
  rut_emisor: string;
  configurada: boolean;
  habilitado: boolean;
  produccion: boolean;
  sucursal: string;
  razon_social: string;
  giro: string;
  direccion: string;
  comuna: string;
  acteco: number | null;
}

export type ConfiguracionDTEUpdate = Omit<
  ConfiguracionDTE,
  "empresa_id" | "rut_emisor" | "configurada"
>;

export const fetchConfigDTE = (empresaId: number): Promise<ConfiguracionDTE> =>
  apiRequest<ConfiguracionDTE>(`/api/v1/superadmin/empresas/${empresaId}/dte`);

export const guardarConfigDTE = (
  empresaId: number,
  datos: ConfiguracionDTEUpdate,
): Promise<ConfiguracionDTE> =>
  apiRequest<ConfiguracionDTE>(`/api/v1/superadmin/empresas/${empresaId}/dte`, {
    method: "PUT",
    body: datos,
  });

